import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Languages,
  Stethoscope,
  Mic,
  Lock,
  BookOpen,
  Users,
  Clock,
  ArrowRight,
  Check,
  Sparkles,
  Heart,
  Activity,
  Brain,
  ChevronDown,
  Globe,
  Mail,
} from "lucide-react";
import { Button } from "../components/ui/button";

const NAV_LINKS = [
  { href: "#features", label: "Features" },
  { href: "#how", label: "How it works" },
  { href: "#pricing", label: "Pricing" },
  { href: "#faq", label: "FAQ" },
];

const FEATURES = [
  {
    icon: Languages,
    title: "Real-time AI translation",
    body: "Claude Sonnet 4.5 translates between 30+ languages with clinical context preserved. No stilted phrasebook output — natural, patient-friendly wording every time.",
  },
  {
    icon: Mic,
    title: "Two-way Conversation Mode",
    body: "Each side has its own mic. Speak naturally; the app transcribes, translates, and reads the result back to the other person — all in under 1 second.",
  },
  {
    icon: Sparkles,
    title: "Hands-free with VAD",
    body: "Keep your hands on the patient. Voice activity detection automatically catches each utterance, so you never reach for the phone mid-session.",
  },
  {
    icon: BookOpen,
    title: "Phrase library, by discipline",
    body: "Pre-loaded with the exact prompts OTs, STs, PTs, COTAs, and PTAs actually use — gait cues, ADL prompts, swallowing instructions. Save your own favorites in one tap.",
  },
  {
    icon: Lock,
    title: "HIPAA-ready by design",
    body: "Patient notes encrypted at rest (Fernet). Every PHI read, write, and translation is logged in an immutable audit trail you can export.",
  },
  {
    icon: Users,
    title: "Built for clinics, too",
    body: "Company plan adds per-seat billing, invite tokens, an admin dashboard, and a shared roster — without breaking each clinician's private patient cards.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Open the patient's card",
    body: "One tap from your roster surfaces last session, preferred language, and any notes — encrypted at rest, decrypted only for you.",
  },
  {
    n: "02",
    title: "Pick a mode",
    body: "Single-phrase for cueing during exercises. Conversation Mode for full back-and-forth. Hands-free for when you need both hands on the patient.",
  },
  {
    n: "03",
    title: "Save what worked",
    body: "Star phrases as favorites. Save the session transcript to the patient log. Audit log keeps everything compliant — no extra workflow.",
  },
];

const DISCIPLINES = [
  { code: "OT", label: "Occupational Therapy", icon: Heart },
  { code: "ST", label: "Speech-Language Pathology", icon: Brain },
  { code: "PT", label: "Physical Therapy", icon: Activity },
  { code: "COTA", label: "Certified OT Assistant", icon: Heart },
  { code: "PTA", label: "PT Assistant", icon: Activity },
];

const PLANS = [
  {
    id: "solo_monthly",
    eyebrow: "Solo",
    name: "$19",
    cadence: "/month",
    blurb: "For individual clinicians.",
    features: [
      "Unlimited translation & TTS",
      "Conversation Mode + hands-free",
      "Phrase library + favorites",
      "Encrypted patient notes",
      "Audit log export",
    ],
    cta: "Start 7-day free trial",
    highlight: false,
  },
  {
    id: "solo_yearly",
    eyebrow: "Solo annual",
    name: "$190",
    cadence: "/year",
    blurb: "Two months free vs. monthly.",
    features: [
      "Everything in Solo",
      "2 months free ($228 → $190)",
      "Same 7-day free trial",
      "Best for full-time clinicians",
    ],
    cta: "Start 7-day free trial",
    highlight: true,
  },
  {
    id: "company_monthly",
    eyebrow: "Company",
    name: "$15",
    cadence: "/seat / month",
    blurb: "For clinics with multiple therapists.",
    features: [
      "Everything in Solo",
      "Invite-link onboarding",
      "Admin dashboard + members",
      "Per-seat billing",
      "Volume discount built-in",
    ],
    cta: "Start 7-day free trial",
    highlight: false,
  },
];

const FAQ = [
  {
    q: "Is this HIPAA-compliant?",
    a: "Clinico Translate is built HIPAA-ready: patient notes are Fernet-encrypted at rest, sessions use httpOnly cookies and per-device revocation, and every PHI read/write hits an immutable audit log. A signed BAA can be arranged for Company-plan customers — email us.",
  },
  {
    q: "Which languages do you support?",
    a: "30+ including Spanish, Mandarin, Cantonese, Arabic, Vietnamese, Tagalog, Russian, French, Haitian Creole, Hindi, Korean, Japanese, Polish, Portuguese, Bengali, Urdu, and more. Speech-to-text and text-to-speech are available in every supported language.",
  },
  {
    q: "Do you actually transcribe well over background noise?",
    a: "We use OpenAI Whisper with built-in noise suppression and echo cancellation. In testing, recognition stays clean over typical clinic-room HVAC and conversation. For very noisy gyms, manual single-utterance mode outperforms hands-free.",
  },
  {
    q: "What devices can I use?",
    a: "Anything with a modern browser and a microphone — iPhone, iPad, Android, MacBook, Windows. We're a PWA, so you can install it to your home screen and use it like a native app.",
  },
  {
    q: "How does the trial work?",
    a: "Pick any plan, enter a card, get 7 days of full access. No charge until day 8. Cancel anytime from your billing portal — no questions, no retention dance.",
  },
  {
    q: "Can I use my own AI keys?",
    a: "No need — translation, transcription, and TTS are all bundled into your subscription. We use Claude Sonnet 4.5 for translation (Opus 4.5 with the AI Power Pack) and OpenAI Whisper + TTS for audio.",
  },
];

export default function Marketing() {
  const [openFaq, setOpenFaq] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const smoothScroll = (e, href) => {
    if (!href.startsWith("#")) return;
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 antialiased">
      <nav
        className={`sticky top-0 z-40 transition-colors ${
          scrolled ? "bg-white/85 backdrop-blur-md border-b border-zinc-200" : "bg-transparent"
        }`}
        data-testid="marketing-nav"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <div className="w-9 h-9 rounded-lg bg-zinc-900 flex items-center justify-center">
              <Languages className="w-4.5 h-4.5 text-white" strokeWidth={2} />
            </div>
            <span className="font-heading text-lg tracking-tight">Clinico Translate</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => smoothScroll(e, l.href)}
                className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/login"
              className="hidden sm:inline-flex h-10 items-center px-4 text-sm font-medium text-zinc-700 hover:text-zinc-900 transition-colors"
              data-testid="nav-signin"
            >
              Sign in
            </Link>
            <Link to="/register" data-testid="nav-start-trial">
              <Button className="h-10 px-4 text-sm">Start free trial</Button>
            </Link>
          </div>
        </div>
      </nav>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 -left-40 w-[600px] h-[600px] bg-emerald-100/40 rounded-full blur-3xl" />
          <div className="absolute top-40 -right-32 w-[500px] h-[500px] bg-amber-100/30 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-zinc-200 bg-white/70 backdrop-blur-sm mb-7" data-testid="hero-eyebrow">
                <Stethoscope className="w-3.5 h-3.5 text-zinc-700" />
                <span className="text-xs uppercase tracking-[0.18em] text-zinc-700 font-medium">
                  Made for OT · ST · PT · COTA · PTA
                </span>
              </div>

              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-zinc-900" data-testid="hero-headline">
                Communicate with every&nbsp;patient.
                <br />
                <span className="text-zinc-500">In every language.</span>
              </h1>

              <p className="mt-7 text-lg lg:text-xl text-zinc-600 leading-relaxed max-w-2xl">
                A real-time translator built for the clinic floor — Spanish, Mandarin, Arabic,
                Vietnamese, and 25+ more. Pre-loaded with the cues OTs, STs, PTs, COTAs, and PTAs
                actually say. Hands-free conversation. HIPAA-ready by default.
              </p>

              <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="hero-cta-row">
                <Link to="/register" data-testid="hero-cta-primary">
                  <Button className="h-12 px-6 text-base group">
                    Start your 7-day free trial
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                  </Button>
                </Link>
                <a
                  href="#how"
                  onClick={(e) => smoothScroll(e, "#how")}
                  className="h-12 inline-flex items-center px-2 text-sm font-medium text-zinc-700 hover:text-zinc-900 underline-offset-4 hover:underline"
                  data-testid="hero-cta-secondary"
                >
                  See how it works →
                </a>
              </div>

              <p className="mt-6 text-sm text-zinc-500" data-testid="hero-trust">
                No credit card hassle — cancel anytime from your billing portal. 30+ languages.
              </p>
            </div>

            <div className="lg:col-span-5 lg:pt-4" data-testid="hero-visual">
              <HeroMock />
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-zinc-200 bg-white" data-testid="disciplines-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
          <p className="text-xs uppercase tracking-[0.22em] text-zinc-500 text-center mb-7">
            Built with — and for — every member of your therapy team
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-5">
            {DISCIPLINES.map((d) => (
              <div key={d.code} className="flex items-center gap-2.5" data-testid={`discipline-${d.code}`}>
                <d.icon className="w-4 h-4 text-zinc-400" />
                <span className="text-sm font-medium text-zinc-700">{d.code}</span>
                <span className="text-sm text-zinc-500 hidden sm:inline">— {d.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="py-24 lg:py-32" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-5">Features</p>
            <h2 className="font-heading text-4xl lg:text-5xl tracking-tight text-zinc-900">
              The translator your phrasebook wishes it was.
            </h2>
            <p className="mt-5 text-lg text-zinc-600 leading-relaxed">
              Every feature was designed alongside working therapists. Nothing extra — just the
              things you actually pull out mid-session.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 rounded-2xl overflow-hidden">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className="bg-white p-7 lg:p-9 hover:bg-zinc-50/60 transition-colors"
                data-testid={`feature-${i}`}
              >
                <div className="w-11 h-11 rounded-lg bg-zinc-900 text-white flex items-center justify-center mb-5">
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading text-xl tracking-tight text-zinc-900 mb-2">{f.title}</h3>
                <p className="text-zinc-600 leading-relaxed text-[15px]">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="how" className="py-24 lg:py-32 bg-zinc-900 text-white relative overflow-hidden" data-testid="how-section">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "20px 20px",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.22em] text-zinc-400 mb-5">How it works</p>
            <h2 className="font-heading text-4xl lg:text-5xl tracking-tight">
              From {`"I don't speak that"`} to {`"got it"`} — in three taps.
            </h2>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-14">
            {STEPS.map((s) => (
              <div key={s.n} className="relative" data-testid={`step-${s.n}`}>
                <div className="font-heading text-7xl text-white/15 tracking-tight">{s.n}</div>
                <h3 className="font-heading text-2xl tracking-tight mt-3 mb-3">{s.title}</h3>
                <p className="text-zinc-400 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-20 rounded-2xl overflow-hidden border border-white/10 bg-black/40 shadow-2xl" data-testid="tutorial-video-card">
            <div className="aspect-video w-full">
              <video
                src="/tutorial.mp4"
                controls
                preload="metadata"
                poster=""
                className="w-full h-full object-cover"
                data-testid="tutorial-video"
              />
            </div>
            <div className="p-5 lg:p-6 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-1">2-minute tour</p>
                <p className="font-heading text-lg tracking-tight">Watch a real session in motion</p>
              </div>
              <Link to="/register" data-testid="tutorial-cta">
                <Button variant="outline" className="bg-transparent text-white border-white/20 hover:bg-white hover:text-zinc-900 h-11">
                  Start free trial
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="py-24 lg:py-32" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-5">Pricing</p>
            <h2 className="font-heading text-4xl lg:text-5xl tracking-tight text-zinc-900">
              Honest pricing. No upsells mid-session.
            </h2>
            <p className="mt-5 text-lg text-zinc-600 leading-relaxed">
              Every plan starts with a 7-day free trial. Cancel anytime — billing portal is one
              click from the dashboard.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {PLANS.map((p) => (
              <div
                key={p.id}
                className={`rounded-2xl border p-7 lg:p-8 flex flex-col ${
                  p.highlight
                    ? "border-zinc-900 bg-zinc-900 text-white shadow-xl scale-[1.02]"
                    : "border-zinc-200 bg-white"
                }`}
                data-testid={`pricing-${p.id}`}
              >
                <p className={`text-xs uppercase tracking-[0.2em] mb-4 ${p.highlight ? "text-zinc-400" : "text-zinc-500"}`}>
                  {p.eyebrow}
                </p>
                <div className="flex items-baseline gap-1.5 mb-3">
                  <span className="font-heading text-5xl tracking-tight">{p.name}</span>
                  <span className={p.highlight ? "text-zinc-400" : "text-zinc-500"}>{p.cadence}</span>
                </div>
                <p className={`text-sm mb-7 ${p.highlight ? "text-zinc-300" : "text-zinc-600"}`}>{p.blurb}</p>
                <ul className="space-y-3 mb-8 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.highlight ? "text-emerald-400" : "text-zinc-900"}`} />
                      <span className={p.highlight ? "text-zinc-200" : "text-zinc-700"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register" data-testid={`pricing-cta-${p.id}`}>
                  <Button
                    variant={p.highlight ? "default" : "outline"}
                    className={
                      p.highlight
                        ? "w-full h-12 bg-white text-zinc-900 hover:bg-zinc-100"
                        : "w-full h-12"
                    }
                  >
                    {p.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-2xl border border-zinc-200 bg-white p-6 lg:p-8 flex items-start lg:items-center justify-between flex-col lg:flex-row gap-5" data-testid="power-pack-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-1">Add-on · $9/mo</p>
                <h3 className="font-heading text-2xl tracking-tight">AI Power Pack</h3>
                <p className="text-zinc-600 mt-1 max-w-xl">
                  Upgrade to Claude Opus 4.5 for the trickiest translations and tts-1-hd voices
                  that sound noticeably more human. Toggle on or off anytime from billing.
                </p>
              </div>
            </div>
            <Link to="/register" className="self-stretch lg:self-auto" data-testid="power-pack-cta">
              <Button variant="outline" className="h-11 w-full lg:w-auto">Add at signup</Button>
            </Link>
          </div>
        </div>
      </section>

      <section id="faq" className="py-24 lg:py-32 bg-white border-t border-zinc-200" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-5 text-center">FAQ</p>
          <h2 className="font-heading text-4xl lg:text-5xl tracking-tight text-zinc-900 text-center mb-14">
            Questions, answered.
          </h2>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <details
                key={i}
                open={openFaq === i}
                onToggle={(e) => {
                  if (e.target.open) setOpenFaq(i);
                  else if (openFaq === i) setOpenFaq(-1);
                }}
                className="group border border-zinc-200 rounded-xl bg-zinc-50/60 open:bg-white open:shadow-sm overflow-hidden"
                data-testid={`faq-${i}`}
              >
                <summary className="cursor-pointer list-none p-5 lg:p-6 flex items-center justify-between gap-4 select-none">
                  <span className="font-heading text-lg tracking-tight text-zinc-900">{item.q}</span>
                  <ChevronDown className="w-5 h-5 text-zinc-400 shrink-0 transition-transform group-open:rotate-180" />
                </summary>
                <div className="px-5 lg:px-6 pb-6 text-zinc-600 leading-relaxed">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-zinc-50" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <Clock className="w-10 h-10 text-zinc-900 mx-auto mb-7" />
          <h2 className="font-heading text-4xl lg:text-6xl tracking-tight leading-tight text-zinc-900">
            Your next patient is waiting.
          </h2>
          <p className="mt-6 text-lg text-zinc-600 max-w-2xl mx-auto">
            Spin up an account, install it to your phone, and bring it into your next session —
            we&apos;ll see you on the other side.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/register" data-testid="final-cta-primary">
              <Button className="h-12 px-7 text-base group">
                Start 7-day free trial
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </Link>
            <Link to="/login" data-testid="final-cta-secondary">
              <Button variant="outline" className="h-12 px-6 text-base">Sign in</Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200 bg-white" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-lg bg-zinc-900 flex items-center justify-center">
                <Languages className="w-4.5 h-4.5 text-white" />
              </div>
              <span className="font-heading text-lg tracking-tight">
                Clinico Translate<sup className="text-[10px] ml-0.5 text-zinc-500">™</sup>
              </span>
            </div>
            <p className="text-zinc-600 leading-relaxed max-w-md text-sm">
              A real-time clinical translator for occupational, speech, and physical therapists.
              Designed for the clinic floor, built HIPAA-ready.
            </p>
            <a
              href="mailto:BrittneyM@clinicotranslate.com"
              className="mt-5 inline-flex items-center gap-2 text-sm text-zinc-700 hover:text-zinc-900"
              data-testid="footer-email"
            >
              <Mail className="w-4 h-4" />
              BrittneyM@clinicotranslate.com
            </a>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">Product</p>
            <ul className="space-y-3 text-sm">
              <li><a href="#features" onClick={(e)=>smoothScroll(e,"#features")} className="text-zinc-600 hover:text-zinc-900">Features</a></li>
              <li><a href="#pricing" onClick={(e)=>smoothScroll(e,"#pricing")} className="text-zinc-600 hover:text-zinc-900">Pricing</a></li>
              <li><a href="#faq" onClick={(e)=>smoothScroll(e,"#faq")} className="text-zinc-600 hover:text-zinc-900">FAQ</a></li>
              <li><Link to="/login" className="text-zinc-600 hover:text-zinc-900">Sign in</Link></li>
              <li><Link to="/register" className="text-zinc-600 hover:text-zinc-900">Start trial</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">Company</p>
            <ul className="space-y-3 text-sm">
              <li><a href="mailto:BrittneyM@clinicotranslate.com" className="text-zinc-600 hover:text-zinc-900">Contact</a></li>
              <li><Link to="/privacy" className="text-zinc-600 hover:text-zinc-900" data-testid="footer-privacy-link">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-zinc-600 hover:text-zinc-900" data-testid="footer-terms-link">Terms of Service</Link></li>
              <li><Link to="/brand" className="text-zinc-600 hover:text-zinc-900" data-testid="footer-brand-link">Press &amp; Brand kit</Link></li>
              <li><a href="mailto:BrittneyM@clinicotranslate.com?subject=BAA%20request" className="text-zinc-600 hover:text-zinc-900">HIPAA / BAA</a></li>
              <li><a href="mailto:BrittneyM@clinicotranslate.com?subject=Press" className="text-zinc-600 hover:text-zinc-900">Press</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-zinc-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-zinc-500" data-testid="footer-copyright">© 2026 Wellness and Lifestyle Strategies LLC. All rights reserved. &quot;Clinico Translate&quot;<sup className="text-[9px]">™</sup> is a trademark of Wellness and Lifestyle Strategies LLC.</p>
            <p className="text-xs text-zinc-500 inline-flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5" /> clinicotranslate.com
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function HeroMock() {
  return (
    <div className="relative" data-testid="hero-mock">
      <div className="absolute -top-3 -left-3 z-10 rounded-full bg-white border border-zinc-200 shadow-sm px-3 py-1.5 text-xs font-medium text-zinc-700 hidden md:flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        English → Spanish
      </div>
      <div className="absolute -bottom-4 -right-2 z-10 rounded-xl bg-zinc-900 text-white shadow-xl px-4 py-3 hidden md:block">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-0.5">Conversation Mode</p>
        <p className="text-sm font-medium">Hands-free · Listening</p>
      </div>

      <div className="rounded-3xl border border-zinc-200 bg-white shadow-2xl shadow-zinc-900/10 overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-zinc-200" />
          <div className="w-2.5 h-2.5 rounded-full bg-zinc-200" />
          <div className="w-2.5 h-2.5 rounded-full bg-zinc-200" />
          <div className="flex-1" />
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Translator</p>
        </div>

        <div className="px-6 pt-6">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-2">You · English</p>
          <p className="text-zinc-900 leading-snug text-[15px]">
            &ldquo;Can you try to stand on one leg for ten seconds while holding the rail?&rdquo;
          </p>
        </div>

        <div className="mx-6 my-5 rounded-xl bg-zinc-900 text-white p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Spanish</p>
            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
              <Mic className="w-3.5 h-3.5 text-white" />
            </div>
          </div>
          <p className="font-medium leading-snug">
            &ldquo;¿Puede intentar pararse en una sola pierna durante diez segundos mientras se sostiene de la barandilla?&rdquo;
          </p>
        </div>

        <div className="px-6 pb-6 flex gap-2 flex-wrap">
          {["Lift your arm", "Open your mouth", "Take a deep breath", "Step forward"].map((p) => (
            <span key={p} className="px-2.5 h-7 inline-flex items-center rounded-md border border-zinc-200 bg-zinc-50 text-xs text-zinc-600">
              {p}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
